import React, { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Dialog } from '@headlessui/react'
import Button from '@atlaskit/button'
import TextField from '@atlaskit/textfield'
import Lozenge from '@atlaskit/lozenge'
import Spinner from '@atlaskit/spinner'
import Tooltip from '@atlaskit/tooltip'
import SearchIcon from '@atlaskit/icon/glyph/search'
import FolderIcon from '@atlaskit/icon/glyph/folder'
import FolderFilledIcon from '@atlaskit/icon/glyph/folder-filled'
import DocumentsIcon from '@atlaskit/icon/glyph/documents'
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right'
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down'
import InfoIcon from '@atlaskit/icon/glyph/info'
import {
  selectLibraryFolderTree,
  selectAllLibraryScenarios,
  selectLibraryScenarios,
  selectSelectedLibraryFolderId,
  selectLibraryFoldersLoading,
  selectLibraryScenariosLoading,
  selectImportInProgress,
  fetchLibraryFolderTreeAsync,
  fetchAllLibraryScenariosAsync,
  fetchLibraryScenariosInFolderAsync,
  selectLibraryFolder,
  bulkCopyLibraryScenariosToReleaseAsync,
  copyLibraryFolderToReleaseAsync,
  toggleLibraryFolderExpand,
  selectExpandedLibraryFolderIds
} from '../../store/slices/librarySlice'
import { fetchScenariosInFolderAsync, fetchAllScenariosAsync, fetchFolderTreeAsync } from '../../store/slices/folderSlice'
import { useNotification } from '../../contexts/NotificationContext'

/**
 * IMPORT FROM LIBRARY MODAL
 *
 * Modal dialog for importing (copying) library scenarios to a release folder.
 * Features:
 * - Left panel: Library folder tree for navigation
 * - Right panel: Scenarios with checkboxes for selection
 * - Import entire folder option
 * - Bulk import selected scenarios
 */
function ImportFromLibraryModal({
  isOpen,
  onClose,
  jiraProjectId,
  releaseId,
  targetFolderId = null
}) {
  const dispatch = useDispatch()
  const { showError, showSuccess, showInfo } = useNotification()

  // Redux state
  const libraryFolderTree = useSelector(selectLibraryFolderTree)
  const allLibraryScenarios = useSelector(selectAllLibraryScenarios)
  const libraryScenarios = useSelector(selectLibraryScenarios)
  const selectedLibraryFolderId = useSelector(selectSelectedLibraryFolderId)
  const expandedFolderIds = useSelector(selectExpandedLibraryFolderIds)
  const foldersLoading = useSelector(selectLibraryFoldersLoading)
  const scenariosLoading = useSelector(selectLibraryScenariosLoading)
  const importInProgress = useSelector(selectImportInProgress)

  // Local state
  const [selectedScenarioIds, setSelectedScenarioIds] = useState(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [localSelectedFolderId, setLocalSelectedFolderId] = useState(null)

  // Determine which scenarios to display
  const displayScenarios = useMemo(() => {
    const baseScenarios = localSelectedFolderId !== null ? libraryScenarios : allLibraryScenarios

    if (!searchQuery.trim()) return baseScenarios

    const query = searchQuery.toLowerCase()
    return baseScenarios.filter(s =>
      s.scenarioName?.toLowerCase().includes(query) ||
      s.description?.toLowerCase().includes(query) ||
      s.scenarioKey?.toLowerCase().includes(query)
    )
  }, [localSelectedFolderId, libraryScenarios, allLibraryScenarios, searchQuery])

  // Fetch library data when modal opens
  useEffect(() => {
    if (isOpen && jiraProjectId) {
      dispatch(fetchLibraryFolderTreeAsync(jiraProjectId))
      dispatch(fetchAllLibraryScenariosAsync(jiraProjectId))
      // Reset selection
      setSelectedScenarioIds(new Set())
      setSearchQuery('')
      setLocalSelectedFolderId(null)
      dispatch(selectLibraryFolder(null))
    }
  }, [isOpen, jiraProjectId, dispatch])

  // Auto-select the first folder when the folder tree loads
  useEffect(() => {
    if (isOpen && libraryFolderTree.length > 0 && localSelectedFolderId === null) {
      const firstFolderId = libraryFolderTree[0].id
      setLocalSelectedFolderId(firstFolderId)
      dispatch(selectLibraryFolder(firstFolderId))
    }
  }, [isOpen, libraryFolderTree, localSelectedFolderId, dispatch])

  // Fetch folder scenarios when folder is selected
  useEffect(() => {
    if (localSelectedFolderId !== null) {
      dispatch(fetchLibraryScenariosInFolderAsync(localSelectedFolderId))
    }
  }, [localSelectedFolderId, dispatch])

  /**
   * HANDLE FOLDER SELECT
   *
   * Selects a library folder to show its scenarios.
   */
  const handleFolderSelect = (folderId) => {
    setLocalSelectedFolderId(folderId)
    dispatch(selectLibraryFolder(folderId))
    // Clear selection when changing folders
    setSelectedScenarioIds(new Set())
  }

  /**
   * HANDLE SELECT ALL
   *
   * Toggles selection of all visible scenarios.
   */
  const handleSelectAll = () => {
    if (selectedScenarioIds.size === displayScenarios.length) {
      // Deselect all
      setSelectedScenarioIds(new Set())
    } else {
      // Select all
      setSelectedScenarioIds(new Set(displayScenarios.map(s => s.id)))
    }
  }

  /**
   * HANDLE SCENARIO TOGGLE
   *
   * Toggles individual scenario selection.
   */
  const handleScenarioToggle = (scenarioId) => {
    setSelectedScenarioIds(prev => {
      const next = new Set(prev)
      if (next.has(scenarioId)) {
        next.delete(scenarioId)
      } else {
        next.add(scenarioId)
      }
      return next
    })
  }

  /**
   * HANDLE IMPORT SELECTED
   *
   * Imports selected scenarios to the target folder in the release.
   */
  const handleImportSelected = async () => {
    if (selectedScenarioIds.size === 0) {
      showInfo('No Selection', 'Please select at least one scenario to import.')
      return
    }

    try {
      // Use correct parameter names that match the thunk's destructuring:
      // thunk expects: { scenarioIds, targetReleaseId, targetFolderId }
      await dispatch(bulkCopyLibraryScenariosToReleaseAsync({
        scenarioIds: Array.from(selectedScenarioIds),
        targetReleaseId: releaseId,
        targetFolderId: targetFolderId
      })).unwrap()

      // Refresh the target folder scenarios
      if (targetFolderId !== null) {
        dispatch(fetchScenariosInFolderAsync(targetFolderId))
      } else {
        dispatch(fetchAllScenariosAsync(releaseId))
      }
      // Refresh folder tree to update scenario counts
      dispatch(fetchFolderTreeAsync(releaseId))

      showSuccess('Import Complete', `Successfully imported ${selectedScenarioIds.size} scenario(s) to your release.`)
      onClose()
    } catch (error) {
      console.error('Failed to import scenarios:', error)
      const errorMessage = error?.message || 'Failed to import scenarios. Please try again.'
      showError('Import Failed', errorMessage)
    }
  }

  /**
   * HANDLE IMPORT FOLDER
   *
   * Imports an entire library folder (with all its scenarios) to the release.
   */
  const handleImportFolder = async () => {
    if (localSelectedFolderId === null) {
      showInfo('No Folder Selected', 'Please select a library folder to import.')
      return
    }

    try {
      // Use correct parameter names that match the thunk's destructuring:
      // thunk expects: { folderId, targetReleaseId, targetParentFolderId }
      await dispatch(copyLibraryFolderToReleaseAsync({
        folderId: localSelectedFolderId,
        targetReleaseId: releaseId,
        targetParentFolderId: targetFolderId
      })).unwrap()

      // Refresh the target folder scenarios
      if (targetFolderId !== null) {
        dispatch(fetchScenariosInFolderAsync(targetFolderId))
      } else {
        dispatch(fetchAllScenariosAsync(releaseId))
      }
      // Refresh folder tree to update scenario counts
      dispatch(fetchFolderTreeAsync(releaseId))

      const folderName = libraryFolderTree.find(f => f.id === localSelectedFolderId)?.name || 'Folder'
      showSuccess('Import Complete', `Successfully imported folder "${folderName}" to your release.`)
      onClose()
    } catch (error) {
      console.error('Failed to import folder:', error)
      const errorMessage = error?.message || 'Failed to import folder. Please try again.'
      showError('Import Failed', errorMessage)
    }
  }

  /**
   * HANDLE MODAL CLOSE
   */
  const handleClose = () => {
    if (!importInProgress) {
      setSelectedScenarioIds(new Set())
      setSearchQuery('')
      setLocalSelectedFolderId(null)
      onClose()
    }
  }

  /**
   * GET PRIORITY APPEARANCE
   *
   * Maps priority to Atlaskit Lozenge appearance.
   */
  const getPriorityAppearance = (priority) => {
    const map = {
      'Critical': 'removed',
      'High': 'moved',
      'Medium': 'inprogress',
      'Low': 'default'
    }
    return map[priority] || 'default'
  }

  /**
   * RENDER FOLDER TREE ITEM
   *
   * Recursively renders folder tree with expand/collapse.
   */
  const renderFolderItem = (folder, depth = 0) => {
    const hasChildren = folder.children && folder.children.length > 0
    const isExpanded = expandedFolderIds.includes(folder.id)
    const isSelected = localSelectedFolderId === folder.id
    const scenarioCount = folder.scenarioCount || 0

    return (
      <div key={folder.id}>
        <div
          className={`library-folder-item ${isSelected ? 'selected' : ''}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 8px',
            paddingLeft: `${8 + depth * 16}px`,
            cursor: 'pointer',
            borderRadius: '4px',
            backgroundColor: isSelected ? 'var(--ds-background-selected, #E9F2FF)' : 'transparent',
            marginBottom: '2px'
          }}
          onClick={() => handleFolderSelect(folder.id)}
        >
          {/* Expand/Collapse chevron */}
          <span
            style={{
              width: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              visibility: hasChildren ? 'visible' : 'hidden'
            }}
            onClick={(e) => {
              e.stopPropagation()
              if (hasChildren) {
                dispatch(toggleLibraryFolderExpand(folder.id))
              }
            }}
          >
            {isExpanded
              ? <ChevronDownIcon label="Collapse" size="small" />
              : <ChevronRightIcon label="Expand" size="small" />
            }
          </span>

          {/* Folder icon */}
          <span style={{ marginRight: '6px', display: 'flex', alignItems: 'center' }}>
            {isSelected
              ? <FolderFilledIcon label="Folder" size="small" primaryColor="var(--ds-icon-brand, #0052CC)" />
              : <FolderIcon label="Folder" size="small" />
            }
          </span>

          {/* Folder name */}
          <span style={{
            flex: 1,
            fontSize: '13px',
            color: isSelected ? 'var(--ds-text-selected, #0052CC)' : 'var(--ds-text, #172B4D)',
            fontWeight: isSelected ? 500 : 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {folder.name}
          </span>

          {/* Scenario count badge */}
          {scenarioCount > 0 && (
            <span style={{
              fontSize: '11px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
              padding: '2px 6px',
              borderRadius: '10px'
            }}>
              {scenarioCount}
            </span>
          )}
        </div>

        {/* Children folders */}
        {hasChildren && isExpanded && (
          <div className="folder-children">
            {folder.children.map(child => renderFolderItem(child, depth + 1))}
          </div>
        )}
      </div>
    )
  }

  // Check if all scenarios are selected
  const allSelected = displayScenarios.length > 0 && selectedScenarioIds.size === displayScenarios.length

  return (
    <Dialog open={isOpen} onClose={handleClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '900px',
            height: '600px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            pointerEvents: 'auto'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)'
            }}
          >
            <Dialog.Title
              style={{
                fontSize: '20px',
                fontWeight: 500,
                color: 'var(--ds-text, #172B4D)',
                margin: 0
              }}
            >
              Import from Library
            </Dialog.Title>
            <p style={{
              fontSize: '13px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              marginTop: '4px',
              marginBottom: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <InfoIcon label="Info" size="small" primaryColor="var(--ds-text-subtlest, #6B778C)" />
              Select scenarios from your library to copy into this release. Original templates remain unchanged.
            </p>
          </div>

          {/* Body - Two Column Layout */}
          <div style={{
            flex: 1,
            display: 'flex',
            overflow: 'hidden'
          }}>
            {/* Left Panel - Library Folders */}
            <div style={{
              width: '260px',
              borderRight: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {/* Folder Panel Header */}
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid var(--ds-border, #DFE1E6)'
              }}>
                <h3 style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'var(--ds-text-subtlest, #6B778C)',
                  margin: 0,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Library Folders
                </h3>
              </div>

              {/* Folder Tree */}
              <div style={{
                flex: 1,
                overflow: 'auto',
                padding: '8px'
              }}>
                {foldersLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <Spinner size="small" />
                  </div>
                ) : libraryFolderTree.length > 0 ? (
                  <>
                    {/* Folder tree */}
                    {libraryFolderTree.map(folder => renderFolderItem(folder))}
                  </>
                ) : (
                  <p style={{
                    fontSize: '13px',
                    color: 'var(--ds-text-subtlest, #6B778C)',
                    textAlign: 'center',
                    padding: '20px'
                  }}>
                    No library folders found.
                    Create folders in the Library page first.
                  </p>
                )}
              </div>

              {/* Import Folder Button */}
              {localSelectedFolderId !== null && (
                <div style={{
                  padding: '12px 16px',
                  borderTop: '1px solid var(--ds-border, #DFE1E6)'
                }}>
                  <Button
                    appearance="default"
                    shouldFitContainer
                    onClick={handleImportFolder}
                    isDisabled={importInProgress}
                    isLoading={importInProgress}
                  >
                    Import Entire Folder
                  </Button>
                </div>
              )}
            </div>

            {/* Right Panel - Scenarios */}
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}>
              {/* Search and Select All Row */}
              <div style={{
                padding: '12px 16px',
                borderBottom: '1px solid var(--ds-border, #DFE1E6)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                {/* Search Input */}
                <div style={{ flex: 1 }}>
                  <TextField
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search scenarios..."
                    elemBeforeInput={
                      <div style={{ paddingLeft: '8px', display: 'flex', alignItems: 'center' }}>
                        <SearchIcon label="Search" size="small" primaryColor="var(--ds-text-subtlest, #6B778C)" />
                      </div>
                    }
                    isCompact
                  />
                </div>

                {/* Select All Checkbox */}
                {displayScenarios.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate = selectedScenarioIds.size > 0 && !allSelected
                        }
                      }}
                      onChange={handleSelectAll}
                      style={{
                        width: '16px',
                        height: '16px',
                        cursor: 'pointer',
                        accentColor: 'var(--ds-background-brand-bold, #0052CC)'
                      }}
                    />
                    <span style={{
                      fontSize: '13px',
                      color: 'var(--ds-text-subtlest, #6B778C)'
                    }}>
                      Select all ({displayScenarios.length})
                    </span>
                  </div>
                )}
              </div>

              {/* Scenario List */}
              <div style={{
                flex: 1,
                overflow: 'auto',
                padding: '12px 16px'
              }}>
                {scenariosLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <Spinner size="medium" />
                  </div>
                ) : displayScenarios.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {displayScenarios.map(scenario => (
                      <div
                        key={scenario.id}
                        className="import-scenario-item"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '10px 12px',
                          borderRadius: '4px',
                          border: `1px solid ${selectedScenarioIds.has(scenario.id) ? 'var(--ds-border-selected, #4C9AFF)' : 'var(--ds-border, #DFE1E6)'}`,
                          backgroundColor: selectedScenarioIds.has(scenario.id) ? 'var(--ds-background-selected, #E9F2FF)' : 'var(--ds-surface, #FFFFFF)',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                        onClick={() => handleScenarioToggle(scenario.id)}
                      >
                        {/* Checkbox */}
                        <div
                          onClick={(e) => e.stopPropagation()}
                          style={{ marginRight: '12px' }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedScenarioIds.has(scenario.id)}
                            onChange={() => handleScenarioToggle(scenario.id)}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                              accentColor: 'var(--ds-background-brand-bold, #0052CC)'
                            }}
                          />
                        </div>

                        {/* Scenario Info */}
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{
                              fontSize: '12px',
                              color: 'var(--ds-text-subtlest, #6B778C)',
                              fontFamily: 'monospace'
                            }}>
                              {scenario.scenarioKey}
                            </span>
                            <span style={{
                              fontSize: '13px',
                              fontWeight: 500,
                              color: 'var(--ds-text, #172B4D)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {scenario.scenarioName}
                            </span>
                          </div>
                          {scenario.description && (
                            <p style={{
                              fontSize: '12px',
                              color: 'var(--ds-text-subtlest, #6B778C)',
                              margin: '4px 0 0',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {scenario.description}
                            </p>
                          )}
                        </div>

                        {/* Metadata */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
                          {scenario.testCaseCount > 0 && (
                            <Tooltip content={`${scenario.testCaseCount} test case(s)`}>
                              <span style={{
                                fontSize: '11px',
                                color: 'var(--ds-text-subtlest, #6B778C)',
                                backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
                                padding: '2px 6px',
                                borderRadius: '3px'
                              }}>
                                {scenario.testCaseCount} {scenario.testCaseCount === 1 ? 'case' : 'cases'}
                              </span>
                            </Tooltip>
                          )}
                          {scenario.priority && (
                            <Lozenge appearance={getPriorityAppearance(scenario.priority)}>
                              {scenario.priority}
                            </Lozenge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '40px',
                    textAlign: 'center',
                    height: '100%'
                  }}>
                    <DocumentsIcon
                      label="No scenarios"
                      size="large"
                      primaryColor="var(--ds-text-subtlest, #6B778C)"
                    />
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'var(--ds-text, #172B4D)',
                      margin: '16px 0 8px'
                    }}>
                      {searchQuery ? 'No matching scenarios' : 'No scenarios in library'}
                    </h3>
                    <p style={{
                      fontSize: '13px',
                      color: 'var(--ds-text-subtlest, #6B778C)',
                      margin: 0
                    }}>
                      {searchQuery
                        ? 'Try a different search term.'
                        : 'Create scenarios in the Library page first.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            {/* Selection count */}
            <span style={{
              fontSize: '13px',
              color: 'var(--ds-text-subtlest, #6B778C)'
            }}>
              {selectedScenarioIds.size > 0
                ? `${selectedScenarioIds.size} scenario(s) selected`
                : 'Select scenarios to import'}
            </span>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                appearance="subtle"
                onClick={handleClose}
                isDisabled={importInProgress}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleImportSelected}
                isLoading={importInProgress}
                isDisabled={selectedScenarioIds.size === 0}
              >
                Import {selectedScenarioIds.size > 0 ? `(${selectedScenarioIds.size})` : ''}
              </Button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export { ImportFromLibraryModal }
